import { format as dateFnsFormat } from 'date-fns';
import { enGB as en, nb, nn } from 'date-fns/locale';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { I18nProvider } from 'react-aria-components';

import { retrieve, save } from '../cookies/cookies';
import engelsk from './strings.en.json';
import bokmal from './strings.nb.json';
import nynorsk from './strings.nn.json';
import { type Params, _S, interpolate } from './translate';

export type Language = 'en' | 'nb' | 'nn';
export type Translation = typeof engelsk;

export const translations: { [key in Language]: Translation } = {
  nb: bokmal,
  en: engelsk,
  nn: nynorsk,
};

const fnslocales = { nb, nn, en };

const htmlLangCodes: { [key in Language]: string } = {
  nb: 'nb-NO',
  nn: 'nn-NO',
  en: 'en',
};

const initialLang = retrieve('posten.signering.lang', 'nb') as Language;

const I18nContext = createContext({
  language: initialLang,
  setLanguage: (_: Language) => {
    return;
  },
});

export const useI18n: () => [
  translation: Translation,
  translate: (textKey: string, params?: Params) => string,
  format: (date: Date, format: string) => string,
  setLanguage: (language: Language) => void,
  language: Language,
  langCode: string,
] = () => {
  const { language, setLanguage } = useContext<{
    language: Language;
    setLanguage: (language: Language) => void;
  }>(I18nContext);

  useEffect(() => {
    document.documentElement.lang = htmlLangCodes[language];
  }, [language]);

  const format = (date: Date | string, formatStr: string) =>
    date === '' || date === undefined
      ? ''
      : dateFnsFormat(new Date(date), formatStr, {
          locale: fnslocales[language],
        });

  const translate = (textKey: string, params?: Params): string =>
    _S(textKey, language, params);

  const langCode = htmlLangCodes[language];

  return [
    translations[language],
    translate,
    format,
    setLanguage,
    language,
    langCode,
  ];
};

export const AppWithI18n = ({ children }: PropsWithChildren) => {
  const [language, setLanguage] = useState<Language>(initialLang);

  const updateLanguage = (language: Language) => {
    save('posten.signering.lang', language);
    setLanguage(language);
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage: updateLanguage }}>
      <I18nProvider locale={language}>{children}</I18nProvider>
    </I18nContext.Provider>
  );
};

export function withParams(text: string, params: Params): string {
  return interpolate(text, params);
}
